import axios from 'axios';

interface UserLogin {
  email: string;
  password: string;
}

interface UserRegistration {
  name: string;
  email: string;
  password: string;
  mobile: string;
  targetYear: number;
  preferredSubjects: string[];
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with CORS credentials support
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors — attempt token refresh before redirecting
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const token = localStorage.getItem('token');

      // No token at all — go to login
      if (!token) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { token },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const newToken: string = data.data.token;
        localStorage.setItem('token', newToken);
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data: UserLogin) => api.post('/auth/login', data),
  register: (data: UserRegistration) => api.post('/auth/register', data),
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data: any) => api.put('/user/profile', data),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) => 
    api.post('/auth/reset-password', { token, password }),
  refreshToken: (token: string) => api.post('/auth/refresh', { token }),
  verifyEmail: (token: string) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: () => api.post('/auth/resend-verification'),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data: any) => api.put('/user/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) => 
    api.put('/user/change-password', data),
  uploadProfileImage: (formData: FormData) => 
    api.post('/user/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  getAnalytics: () => api.get('/user/analytics'),
};

// Test API
export const testAPI = {
  getTests: () => api.get('/tests'),
  getTest: (id: string) => api.get(`/tests/${id}`),
  startTest: (id: string) => api.post(`/tests/${id}/start`),
  getTestQuestions: (id: string) => api.get(`/tests/${id}/questions`),
  getTestAttempt: (testId: string, attemptId: string) => 
    api.get(`/tests/${testId}/attempt/${attemptId}`),
  saveAnswer: (id: string, data: any) => api.post(`/tests/${id}/answer`, data),
  submitTest: (id: string, data: any) => api.post(`/tests/${id}/submit`, data),
  getTestStatus: (testId: string, attemptId: string) => 
    api.get(`/tests/${testId}/status/${attemptId}`),
  getTestResults: (attemptId: string) => api.get(`/tests/results/${attemptId}`),
  getTestAttempts: (testId: string) => api.get(`/tests/${testId}/attempts`),
  getUsage: () => api.get('/tests/usage'),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getStats: () => api.get('/admin/stats'),
  
  // Users
  getUsers: (params?: any) => api.get('/admin/users', { params }),
  getUserDetails: (id: string) => api.get(`/admin/users/${id}`),
  updateUserStatus: (id: string, isActive: boolean) => 
    api.put(`/admin/users/${id}/status`, { isActive }),
  
  // Test Attempts
  getTestAttempts: (params?: any) => api.get('/admin/test-attempts', { params }),
  
  // Test Management
  getTests: (params?: any) => api.get('/admin/tests', { params }),
  getTestStats: () => api.get('/admin/tests/stats'),
  createTest: (data: any) => api.post('/admin/tests', data),
  getTestDetails: (id: string) => api.get(`/admin/tests/${id}`),
  updateTest: (id: string, data: any) => api.put(`/admin/tests/${id}`, data),
  deleteTest: (id: string) => api.delete(`/admin/tests/${id}`),
  toggleTestStatus: (id: string) => api.patch(`/admin/tests/${id}/toggle-status`),
  setupTestsTable: () => api.post('/admin/setup-tests-table'),
  
  // Question Management
  getQuestions: (params?: any) => api.get('/admin/questions', { params }),
  getQuestionStats: () => api.get('/admin/questions/stats'),
  createQuestion: (data: any) => api.post('/admin/questions', data),
  getQuestionDetails: (id: string) => api.get(`/admin/questions/${id}`),
  updateQuestion: (id: string, data: any) => api.put(`/admin/questions/${id}`, data),
  deleteQuestion: (id: string) => api.delete(`/admin/questions/${id}`),
  getTopics: (subject: string) => api.get(`/admin/questions/topics/${subject}`),
  bulkImportQuestions: (data: any) => api.post('/admin/questions/bulk-import', data),
  
  // Test-Question Management
  getTestQuestions: (testId: string) => api.get(`/admin/tests/${testId}/questions`),
  addQuestionsToTest: (testId: string, questionIds: number[]) => 
    api.post(`/admin/tests/${testId}/questions`, { questionIds }),
  removeQuestionsFromTest: (testId: string, questionIds: number[]) => 
    api.delete(`/admin/tests/${testId}/questions`, { data: { questionIds } }),
  reorderTestQuestions: (testId: string, questionOrders: any[]) => 
    api.put(`/admin/tests/${testId}/questions/reorder`, { questionOrders }),
  autoGenerateQuestions: (testId: string, criteria: any) => 
    api.post(`/admin/tests/${testId}/auto-generate`, criteria),
  
  // Admin Management
  createAdmin: () => api.post('/admin/create-admin'),
};

// Practice API
export const practiceAPI = {
  // Topics
  getAvailableTopics: (subject: string) => api.get(`/practice/topics/${subject}`),
  getTopicRecommendations: (subject: string) => api.get(`/practice/recommendations/${subject}`),
  
  // Practice Tests
  createTopicTest: (subject: string, topic: string, difficulty: string, questionCount: number) =>
    api.post('/practice/create-topic-test', { subject, topic, difficulty, questionCount }),
  createPreviousYearTest: (subject: string, year: number, questionCount: number) =>
    api.post('/practice/create-previous-year-test', { subject, year, questionCount }),
  
  // Previous Years
  getAvailablePreviousYears: (subject: string) => api.get(`/practice/previous-years/${subject}`),
  
  // Analytics
  getComprehensiveAnalytics: () => api.get('/practice/analytics/comprehensive'),
  getPracticeHistory: (limit?: number) => api.get('/practice/history', { params: { limit } }),
};

// Analytics API
export const analyticsAPI = {
  getDashboardStats: () => api.get('/analytics/dashboard'),
  getOverview: () => api.get('/analytics/overview'),
  getTestSummaries: () => api.get('/analytics/test-summaries'),
};

export default api;
